import { jsx as _jsx } from "react/jsx-runtime";
import { PaymentProcessor } from "../../../Account/BillingInformation/Shared/types/PaymentProcessorTypes";
import { ModalContent } from "../../../Shared/components/Modal";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
import { ERROR_MESSAGE_GENERIC } from "../../../Shared/utils/constants";
import { calculateTotal, confirmThenUpdateOrder, updateOrder } from "../../utils/requests";
import { LoadingText } from "./PaymentDialog-types";
/**
 * Handles the click event of the confirm button in the PaymentDialog
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @returns {(dataForUpdate: UpdateOrderParams, onPaymentComplete: () => void) => void}
 * Function to process order update and handle completion.
 */
export const handleConfirmClick = (actions) => (dataForUpdate, onPaymentComplete) => {
    const { setIsLoading, setIsFormSubmitted } = actions;
    setIsLoading(LoadingText.COMPLETING);
    setIsFormSubmitted(true);
    updateOrder(dataForUpdate.days, dataForUpdate.orderId, true, dataForUpdate.paymentMethodId ? dataForUpdate.paymentMethodId : undefined, dataForUpdate.recipeIngredientReplacementMode, dataForUpdate.paymentProcessor, dataForUpdate.paymentIntentId, handleResponse(dataForUpdate, onPaymentComplete, actions), handleError(dataForUpdate, actions), dataForUpdate.giftCardId);
};
/**
 * Handles the API response for the update order process.
 * @param {UpdateOrderParams} dataForUpdate - Data for the order being updated.
 * @param {() => void} onPaymentComplete - Callback triggered on successful payment completion.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @returns {(res: AxiosResponse<any>) => void} Function to process the API response.
 */
const handleResponse = (dataForUpdate, onPaymentComplete, actions) => (res) => {
    if (res.data.success) {
        updateSuccess(dataForUpdate, actions, onPaymentComplete)(res);
    }
    else if (res.data.extraStep) {
        handleExtraStep(actions.showModal, res, dataForUpdate, updateSuccess(dataForUpdate, actions, onPaymentComplete), handleError(dataForUpdate, actions));
    }
    else {
        updateFail(dataForUpdate, actions, res.data.message);
    }
};
/**
 * Handles the extra step if required for Stripe and Tap
 * @param {(props: ModalProps) => void} showModal - Function to display the modal dialog.
 * @param {AxiosResponse<any>} res - API response containing extra step details.
 * @param {UpdateOrderParams} dataForUpdate - Data for the order being updated.
 * @param {(res: AxiosResponse<any>) => void} onSuccess - Callback triggered on successful completion of the extra step.
 * @param {(error: AxiosError<any>) => void} onError - Callback triggered on error during the extra step.
 */
export const handleExtraStep = (showModal, res, dataForUpdate, onSuccess, onError) => {
    const paymentProcessorString = PaymentProcessor[dataForUpdate.paymentProcessor].toLowerCase();
    const paymentProcessorResponse = res.data[paymentProcessorString];
    showModal({
        title: "Payment Confirmation",
        content: (_jsx(ModalContent, { children: _jsx("iframe", { style: { height: "calc(75vh - 70px - 64px - 20px)", overflow: "scroll", border: "none", width: "100%" }, src: paymentProcessorResponse.redirectToUri }) })),
        closeModalFunc: () => {
            confirmThenUpdateOrder(dataForUpdate.days, dataForUpdate.orderId, true, dataForUpdate.paymentMethodId, dataForUpdate.recipeIngredientReplacementMode, dataForUpdate.paymentProcessor, paymentProcessorResponse.paymentIntentId, paymentProcessorResponse.chargeId, onSuccess, onError);
        },
        maxWidth: "sm",
        fullWidth: true,
    });
};
/**
 * Handles errors during the update order process and logs it
 * @param {UpdateOrderParams} dataForUpdate - Data for the order being updated.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 */
const handleError = (dataForUpdate, actions) => (error) => {
    const { datadogLog, setIsLoading, setIsErrorState } = actions;
    datadogLog("My Meals updateOrder error in paymentDialog", LogLevel.WARN, { orderId: dataForUpdate.orderId, days: dataForUpdate.days }, error);
    setIsLoading(undefined);
    setIsErrorState(ERROR_MESSAGE_GENERIC);
};
/**
 * Handles successful update of an order.
 * @param {UpdateOrderParams} dataForUpdate - Data for the order being updated.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @param {() => void} onPaymentComplete - Callback triggered on successful payment completion.
 */
const updateSuccess = (dataForUpdate, actions, onPaymentComplete) => (res) => {
    var _a;
    const { setIsLoading, datadogLog, setIsErrorState, setPaymentComplete, clearState } = actions;
    setIsLoading(undefined);
    if (!((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success)) {
        datadogLog("My Meals updateOrder unsuccessful in paymentDialog", LogLevel.ERROR, {
            orderId: dataForUpdate.orderId,
            days: dataForUpdate.days,
        });
        setIsErrorState(res.data.responseMsg ? res.data.responseMsg : ERROR_MESSAGE_GENERIC);
    }
    else {
        setPaymentComplete(true);
        setTimeout(() => {
            clearState();
            onPaymentComplete();
        }, 2500);
    }
};
/**
 * Handles failed updates of an order and logs it
 * @param {UpdateOrderParams} dataForUpdate - Data for the order being updated.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @param {string} [errorMessage] - Optional error message to display.
 */
const updateFail = (dataForUpdate, actions, errorMessage) => {
    const { datadogLog, setIsLoading, setIsErrorState } = actions;
    datadogLog("My Meals updateOrder failed in paymentDialog", LogLevel.ERROR, {
        orderId: dataForUpdate.orderId,
        days: dataForUpdate.days,
    });
    setIsLoading(undefined);
    setIsErrorState(errorMessage ? errorMessage : ERROR_MESSAGE_GENERIC);
};
/**
 * Calculates the order total based on the given model.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 */
export const calculateOrderTotal = (actions) => (model) => {
    const { setIsLoading } = actions;
    setIsLoading(LoadingText.CALCULATING);
    calculateTotal(model, onCalculateSuccess(actions, model), onCalculateError(actions, model));
};
/**
 * Handles successful calculation response.
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @param {CalculateTotalModel} model - The model containing the order details.
 */
const onCalculateSuccess = (actions, model) => (res) => {
    var _a;
    const { datadogLog, setIsErrorState, setIsLoading, setBalance } = actions;
    if (!((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success)) {
        datadogLog("My Meals calculation failure in paymentDialog", LogLevel.ERROR, {
            orderId: model.OrderId,
            days: model.Days,
        });
        setIsErrorState(ERROR_MESSAGE_GENERIC);
        setIsLoading(undefined);
        return;
    }
    setBalance(res.data.balance);
    setIsLoading(undefined);
};
/**
 * Handles errors during the calculation of the order total and logs it
 * @param {PaymentActionHelpers} actions - Object containing helper functions for payment actions.
 * @param {CalculateTotalModel} model - The model containing the order details.
 */
const onCalculateError = (actions, model) => (error) => {
    const { datadogLog, setIsErrorState, setIsLoading } = actions;
    datadogLog("My Meals calculation error in paymentDialog", LogLevel.WARN, { orderId: model.OrderId, days: model.Days }, error);
    setIsLoading(undefined);
    setIsErrorState(ERROR_MESSAGE_GENERIC);
};
